import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';

const partners = [
  { name: 'CraftingPat', image: '/images/partners/craftingpat.png' },
  { name: 'Partner 1',   image: '/images/partners/partner-1.png' },
  { name: 'NG',          image: '/images/partners/ng-avatar.png' },
  { name: 'Phill',       image: '/images/partners/phil.png' },
  { name: 'ZeLa',        image: '/images/partners/zela.png' },
];

export const Partners = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % partners.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + partners.length) % partners.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 4000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  return (
    <section className="section-spacing relative overflow-hidden" data-testid="partners-section">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5" />
      
      <div className="container-main relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              {t('partners.title')}
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-md">
              {t('partners.subtitle')}
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="mailto:partner@deinserverhost.de">
                <Button className="bg-primary hover:bg-primary/90 font-bold uppercase tracking-wider glow-hover">
                  {t('partners.inquire')}
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <Button variant="outline" className="border-primary/30 hover:border-primary">
                {t('partners.viewAll')}
              </Button>
            </div>
          </motion.div>

          {/* Right - Carousel */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            
            className="relative"
          >
            <div className="glass rounded-2xl p-8 overflow-hidden">
              {/* Carousel */}
              <div className="relative h-48 flex items-center justify-center">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentIndex}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.3 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <img
                      src={partners[currentIndex].image}
                      alt={partners[currentIndex].name}
                      className="max-h-32 max-w-full object-contain"
                    />
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-6">
                <button
                  onClick={prevSlide}
                  className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                  aria-label="Previous partner"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                {/* Dots */}
                <div className="flex items-center gap-2">
                  {partners.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === currentIndex ? 'bg-primary' : 'bg-white/20'
                      }`}
                      aria-label={`Go to partner ${index + 1}`}
                    />
                  ))}
                </div>

                <button
                  onClick={nextSlide}
                  className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                  aria-label="Next partner"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Partners;
